import type { WorldState } from "@/core";
import type { GameApp } from "../app/GameApp";
import {
  BRIDGE_CHANNEL,
  BRIDGE_VERSION,
  type BridgeEnvelope,
  type GameReadyPayload,
  type HostSetConfigPayload,
} from "./protocol";

export interface IframeBridgeConfig {
  gameVersion: string;
}

export class IframeBridge {
  private targetOrigin: string;
  private configuredTransport: "mock" | "server" = "mock";

  constructor(private cfg: IframeBridgeConfig) {
    this.targetOrigin = this.detectTargetOrigin();
  }

  attach(app: GameApp) {
    app.onWorldState((s) => this.send({ type: "stateUpdate", payload: s } satisfies PartialEnvelope<WorldState>));

    window.addEventListener("message", (ev) => this.onMessage(app, ev));

    const ready: GameReadyPayload = {
      gameVersion: this.cfg.gameVersion,
      capabilities: {
        postMessage: true,
        transport: this.configuredTransport,
      },
    };
    this.send({ type: "ready", payload: ready } satisfies PartialEnvelope<GameReadyPayload>);
  }

  private onMessage(_app: GameApp, ev: MessageEvent) {
    if (!this.isAllowed(ev)) return;
    const data = ev.data as BridgeEnvelope;
    if (!data || data.channel !== BRIDGE_CHANNEL || data.version !== BRIDGE_VERSION) return;

    if (data.type === "setConfig") {
      const p = data.payload as HostSetConfigPayload;
      if (!p?.roomId || !p?.apiBaseUrl) return;
      this.configuredTransport = "server";
      const ready: GameReadyPayload = {
        gameVersion: this.cfg.gameVersion,
        capabilities: { postMessage: true, transport: this.configuredTransport },
      };
      this.send({ type: "ready", payload: ready } satisfies PartialEnvelope<GameReadyPayload>);
      return;
    }

    if (data.type === "start") {
      return;
    }

    if (data.type === "pause") {
      return;
    }
  }

  private detectTargetOrigin(): string {
    try {
      if (document.referrer) return new URL(document.referrer).origin;
    } catch {
    }
    return window.location.origin;
  }

  private isAllowed(ev: MessageEvent) {
    if (ev.source !== window.parent) return false;
    if (!ev.origin) return false;
    if (ev.origin !== this.targetOrigin) return false;
    return true;
  }

  private send<TPayload>(msg: PartialEnvelope<TPayload>) {
    if (window.parent === window) return; 
    const env: BridgeEnvelope<TPayload> = {
      channel: BRIDGE_CHANNEL,
      version: BRIDGE_VERSION,
      type: msg.type,
      requestId: msg.requestId,
      payload: msg.payload,
    };
    window.parent.postMessage(env, this.targetOrigin);
  }
}

type PartialEnvelope<TPayload> = Pick<BridgeEnvelope<TPayload>, "type" | "payload" | "requestId">;

